'use client'

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import {
  calcIsTreatment,
  EXPERIMENT_NAMES,
  ExperimentName,
  EXPERIMENTS,
} from 'app/_hooks/useExperiment'
import { createContext, ReactNode, useEffect, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import browserEnv from '~/libs/env/browserEnv'
import { Switch } from './Switch'

type ExperimentsContextType = Record<ExperimentName, boolean> | null

export const ExperimentsContext = createContext<ExperimentsContextType>(null)

export const ExperimentsSwitcher = ({ children }: { children: ReactNode }) => {
  const [experiments, setExperiments] = useState<ExperimentsContextType>(null)
  useEffect(() => {
    let initialExperiments: Partial<Record<ExperimentName, boolean>> = {}

    EXPERIMENT_NAMES.forEach((experimentName) => {
      const storageValue = localStorage.getItem(`experiment_${experimentName}`)
      const isTreatment = storageValue ? storageValue === 'true' : calcIsTreatment(experimentName)
      initialExperiments[experimentName] = isTreatment
    })

    setExperiments(initialExperiments as Record<ExperimentName, boolean>)
  }, [])

  const [open, setOpen] = useState(false)

  const lastClickTimeRef = useRef<number | null>(null)
  const countRef = useRef(0)

  useEffect(() => {
    const onClick = () => {
      const now = Date.now()

      if (lastClickTimeRef.current !== null && now - lastClickTimeRef.current < 1000) {
        countRef.current += 1
      } else {
        // 最初のクリック or 1秒以上空いた場合はリセット
        countRef.current = 1
      }

      lastClickTimeRef.current = now

      if (countRef.current >= 4) {
        setOpen(true)
        countRef.current = 0
        lastClickTimeRef.current = null
      }
    }

    window.addEventListener('click', onClick)
    return () => window.removeEventListener('click', onClick)
  }, [])

  if (typeof navigator !== 'undefined' && navigator.webdriver) {
    return <>{children}</>
  }

  if (browserEnv.NEXT_PUBLIC_ENV === 'production') {
    return <>{children}</>
  }

  if (!experiments) {
    return <>{children}</>
  }

  return (
    <ExperimentsContext.Provider value={experiments}>
      {children}
      <Dialog open={open}>
        <DialogTitle>A/Bテスト一覧</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {EXPERIMENT_NAMES.map((key) => (
              <Stack key={key} direction='row' spacing={2}>
                <Switch
                  value={experiments[key]}
                  onChange={() => {
                    experiments[key] = !experiments[key]
                    localStorage.setItem(`experiment_${key}`, String(experiments[key]))
                    setExperiments({ ...experiments })
                  }}
                />
                <Stack>
                  <Typography variant='h2'>{key}</Typography>
                  <Typography variant='body2'>{EXPERIMENTS[key]}</Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color='inherit'>
            <Trans i18nKey='close' />
          </Button>
        </DialogActions>
      </Dialog>
    </ExperimentsContext.Provider>
  )
}
